import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {deleteTeam, getTeams} from '../../stores/teams'
import {ImgButton} from '../../components/core/button'
import {EditIcon, HorizontalMoreIcon, PlusIcon, TrashIcon, UsersIcon} from '../../icons'
import Customer from '../../utils/customer'
import ContentLoader from '../../components/core/loader'
import Table from '../../components/core/table'
import TeamForm from './TeamForm'
import Modal from '../../components/core/modal'
import {setConfirmIsActive} from '../../stores/alert'
import Confirm from '../../components/core/confirm'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import {routes} from '../../routes'
import {Link} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import Badge from '../../components/core/badge'
import {DateFormats, formatDate} from '../../utils/date'
import Tooltip from '../../components/core/tooltip'
import {colors} from '../../utils/colors'
import classNames from 'classnames'
import {sortBySelected} from '../../utils/sorting'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'
import Footer from '../../components/layouts/footer'

const SORT_ID = 'teams'

const {isReadOnlyModeEnabled, isIntegrationEnabled} = envConfig

const Teams = () => {
    const isRootMember = Customer.isRoot()
    const text = useNamespaceTranslations('teams')
    const isManager = Customer.isManager()
    const [selectedTeams, setSelectedTeams] = useState([])
    const [clickedTeam, setClickedTeam] = useState(null)
    const [teamForm, setTeamForm] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isFetching, setIsFetching] = useState(false)

    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const teams = useSelector(state => state.teams.teams)
    const [sortedData, setSortedData] = useState([])

    const dispatch = useDispatch()

    const team = sortedData?.find(item => item?.team_id === selectedTeams[0])

    const ActionButtons = ({team}) => {
        return (
            <Dropdown menuButton={<button className={classNames('table-action-btn', {
                dark: isDarkMode
            })}><HorizontalMoreIcon/></button>}>
                <MenuItem disabled={getEditButtonStatus(team) || isReadOnlyModeEnabled} onClick={() => {
                    setClickedTeam(team)
                    setTeamForm({isEdit: true})
                }
                }>
                    <span className="d-flex"><EditIcon/></span>
                    <span>{text.edit}</span>
                </MenuItem>
                {isRootMember && (
                    <>
                        <MenuDivider/>
                        <MenuItem disabled={!isRootMember || isReadOnlyModeEnabled} className="danger" onClick={() => {
                            setClickedTeam(team)
                            dispatch(setConfirmIsActive(true))
                        }
                        }>
                            <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                            <span>{text.delete}</span>
                        </MenuItem>
                    </>
                )}
            </Dropdown>
        )
    }

    const tableHeader =  [
        {
            label: text.name,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: '',
            className: 'min-width',
            sorting: null
        },
        {
            label: text.member_count,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'member_count_asc',
                    field: ['member_count']
                },
                desc: {
                    key: 'member_count_desc',
                    field: ['member_count']
                }
            }
        },
        {
            label: text.created,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'created_at_asc',
                    field: ['created_at']
                },
                desc: {
                    key: 'created_at_desc',
                    field: ['created_at']
                }
            }
        }
    ]

    const tableBody = sortedData?.map(item => {
        const url = NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.members.paths), ':teamId', item?.team_id)
        return [
            <span className='min-width'>
                <Link className="underlined" to={url}>{item?.name}</Link>
            </span>,
            ...item?.is_member ? [<Badge color="success">Current</Badge>] : [null],
            <span className='min-width'><Badge color="primary">{item?.member_count}</Badge></span>,
            <div className="flex-1">
                <span>{formatDate(item?.created_at, DateFormats.SLASH_DMY_TIME_SHORT)}</span>
                <span className="d-flex align-items-center gap-half">
                    <Tooltip content={text.members}>
                        <span className="button-field">
                            <Link
                                to={url}
                            >
                                <UsersIcon color={colors.colorPrimary} className="users-icon"/>
                            </Link>
                        </span>
                    </Tooltip>
                    {(isRootMember || isManager) && <ActionButtons team={item}/>}
                </span>
            </div>
        ]
    })

    const fetchData = async(showLoader = true) => {
        setIsFetching(showLoader)
        await dispatch(getTeams())
        setIsFetching(false)
    }

    const openModal = () => {
        setTeamForm(!teamForm)
    }

    const deleteTeams = async() => {
        setIsDeleting(true)
        const success = await dispatch(deleteTeam(clickedTeam?.team_id || selectedTeams[0]))
        setIsDeleting(false)
        setClickedTeam(null)
        dispatch(setConfirmIsActive(false))
        if (success) {
            setSelectedTeams([])
            fetchData(false)
        }
    }

    const closeModal = () => {
        fetchData(false)
        setTeamForm(false)
        setClickedTeam(null)
    }

    const openConfrim = () => {
        setClickedTeam(null)
        dispatch(setConfirmIsActive(true))
    }

    const getEditButtonStatus = data => {
        const localTeam = data || team
        if (!data && selectedTeams.length !== 1) {
            return true
        }
        if (isManager) {
            return !localTeam?.is_member
        }
        return !isRootMember
    }

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        setSortedData(sortBySelected(teams, SORT_ID, tableHeader))
    }, [teams])

    return (
        <main className={classNames('main teams', {
            dark: isDarkMode
        })}>
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{isIntegrationEnabled ? text.section : text.teams}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {isRootMember && (
                        <ImgButton onClick={openModal} disabled={isReadOnlyModeEnabled}>
                            {text.add_team}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    )}
                </div>
                {isFetching ? (
                    <ContentLoader/>
                ) : (
                    <div>
                        <Modal isOpen={Boolean(teamForm)} title={teamForm?.isEdit ? text.edit_team : text.add_new_team}
                            closeModal={() => {
                                setClickedTeam(null)
                                setTeamForm(false)
                            }}>
                            <TeamForm team={clickedTeam || team} modal={teamForm} closeModal={closeModal}
                                teamLength={sortedData?.length}/>
                        </Modal>
                        <Confirm
                            body={text.are_you_sure}
                            callback={deleteTeams} showLoader={isDeleting}/>
                        {(isRootMember || isManager) && (
                            <div className="search-filter-actions">
                                <div className="selected-actions">
                                    {isRootMember && (
                                        <ImgButton onClick={openModal}
                                            className="responsive-add-button">
                                            <span><PlusIcon/></span>
                                        </ImgButton>
                                    )}
                                    <ImgButton className={classNames({
                                        'light-bg': !isDarkMode
                                    })}
                                    disabled={getEditButtonStatus() || isReadOnlyModeEnabled}
                                    onClick={() => setTeamForm({isEdit: true})}>
                                        <span><EditIcon/></span>
                                        <span className="button-text">{text.edit}</span>
                                    </ImgButton>
                                    {isRootMember && (
                                        <ImgButton className={classNames('outline-red', {
                                            'light-bg': !isDarkMode
                                        })}
                                        disabled={!isRootMember || selectedTeams.length !== 1 || isReadOnlyModeEnabled}
                                        onClick={openConfrim}>
                                            <span><TrashIcon stroke={colors.colorRed}/></span>
                                            <span className="button-text">{text.delete}</span>
                                        </ImgButton>
                                    )}
                                </div>
                            </div>
                        )}
                        <Table
                            strech
                            tableHeader={tableHeader}
                            tbodyData={tableBody}
                            identificator='team_id'
                            hasCheckbox={isRootMember || isManager}
                            data={sortedData}
                            isMultiCheckable={false}
                            selectedData={selectedTeams}
                            setSelectedData={data => setSelectedTeams(data)}
                            isSortingEnabled
                            setData={setSortedData}
                            sortId={SORT_ID}
                            originalData={teams}
                        />
                    </div>
                )}
            </div>
            <Footer />
        </main>
    )
}

export default Teams
